<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <export-sidebar
          sidebar-id="sidebar-workers-export"
          storage-key="sidebar_workers_export"
          :columns="exportableFields"
          :data-to-export="dataToExport"
          :data-mapper="mapWorkerForExport"
          file-name="Workers.xlsx"
          sheet-name="Workers"
          :is-busy="isBusy"
          :items-count="totalRows"
        />
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
        <b-button v-if="canEditWorkers()" class="custom-btn-primary" :disabled="isBusy" @click="onCreate()">
          <b-spinner v-if="isBusy" small/>
          <v-icon name="buttons/plus" :size="12"/>
          Новый исполнитель
        </b-button>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col md="6" cols="12">
            <label class="small text-muted">Города</label>
            <b-form-select
              id="input-town"
              size="sm"
              v-model="filters.town"
            >
              <option :value="-1">Все города</option>
              <option v-for="option in towns" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
          <b-col md="6" cols="12">
            <label class="small text-muted">Уровень КПД</label>
            <b-form-select
              id="input-level-efficiency"
              size="sm"
              v-model="filters.level_efficiency"
              :options="levelEfficiencyOptions"
            />
          </b-col>
          <b-col md="6" cols="12">
            <label class="small text-muted">Активные</label>
            <b-form-select
              id="input-active"
              size="sm"
              v-model="filters.active"
            >
              <option :value="null">Показывать все</option>
              <option :value="true">Активные</option>
              <option :value="false">Удалённые</option>
            </b-form-select>
          </b-col>
          <b-col md="6" cols="12">
            <label class="small text-muted">Статус</label>
            <b-form-select
              id="input-status"
              size="sm"
              v-model="filters.status_id"
            >
              <option :value="null">Все статусы</option>
              <option v-for="option in workerStatuses" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="workers"
            ref="workers"
            class="custom-table text-small mb-0"
            :tbody-tr-class="tbodyRowClass"
            sticky-header
            select-mode="multi"
            responsive
            :busy="isBusy"
            :items="items"
            :fields="renderedFields"
            no-provider-filtering
            no-provider-paging
            no-provider-sorting
            no-local-sorting
            @sort-changed="sortChanged"
            @row-clicked="onRowClicked"
            @row-dblclicked="onRowDblClicked"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="5"/>
            </template>

            <template #head(selected)="row">
              <b-form-checkbox
                class="mx-1"
                v-model="allSelected"
                :indeterminate="indeterminate"
                size="md"
                @change="selectPageRows"
              />
            </template>

            <template #cell(selected)="row">
              <b-form-checkbox
                class="mx-1"
                size="md"
                :checked="row.item.selected"
                @click.native.stop
                @change="onRowClicked(row.item)"
              />
            </template>

            <template #cell(phone)="row">
              <a :href=telUrl(row.item.phone) style="color: #1A9167 !important;">
                <v-icon name="buttons/phone" :size="12" class="mr-1"/>
                {{phoneString(row.item.phone)}}
              </a>
            </template>

            <template #cell(sex)="row">
              {{ typeof row.item.sex === 'boolean' ? (row.item.sex ? 'М' : 'Ж') : '' }}
            </template>

            <template #cell(level_efficiency)="row">
              <span :id="'level-efficiency-badge-' + row.item.id" :class="['custom-tag', getLevelEfficiencyStyle(row.item.level_efficiency)]">
                  {{getLevelEfficiencyText(row.item.level_efficiency)}}
                </span>
              <b-tooltip
                v-if="row.item.description !== ''"
                :target="'level-efficiency-badge-' + row.item.id"
                triggers="hover"
                :variant="getLevelEfficiencyStyle(row.item.level_efficiency)"
                noninteractive
              >
                {{ row.item.description }}
              </b-tooltip>
            </template>

            <template #cell(employment_type)="row">
              {{getEmploymentTypeText(row.item.employment_type)}}
            </template>

            <template #cell(employment_type_smz)="row">
              {{ row.item.employment_type === 1 ? 'Да' : 'Нет' }}
            </template>

            <template #cell(passport_url)="row">
              <a v-if="row.item.passport_url.length > 0" @click="downloadFile(row.item.id, row.item.passport_url)">
                <v-icon name="buttons/passport" :size="19" :style="{color:'#1A9167'}"/>
              </a>
            </template>

            <template #cell(active)="row">
              <v-icon name="buttons/cross" v-if="!row.item.active" :size="13"/>
              <v-icon name="buttons/check" v-if="row.item.active" :size="14"/>
            </template>

            <template #cell(action)="row">
              <b-dropdown variant="link" toggle-class="text-decoration-none box-shadow-none" no-caret class="custom-dropdown">
                <template #button-content>
                  <v-icon name="buttons/ellipsis_vertical" :size="15"/>
                </template>
                <b-dropdown-item variant="success" @click.stop="onView(row.item)" target="_blank">
                  <v-icon name="buttons/eye" :size="14" class="mr-2"/>
                  Смотреть
                </b-dropdown-item>
                <b-dropdown-item variant="primary" v-if="canEditWorkers()" @click="onEdit(row.item)">
                  <v-icon name="buttons/edit" :size="14" class="mr-2"/>
                  Редактировать
                </b-dropdown-item>
                <b-dropdown-item variant="danger" @click="onRemove(row)">
                  <v-icon name="buttons/trash" :size="14" class="mr-2"/>
                  Удалить
                </b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            :total-rows="totalRows"
            :per-page.sync="perPage"
            :current-page.sync="currentPage"
            use-router
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-update>
              <span v-if="selected.length > 0" class="mr-2">
                Выбрано: <b>{{selected.length}}</b>
              </span>
              <span v-if="selected.length > 0" class="vertical-divider"/>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
        <v-icon name="navbar/empty_table" :size="90"/>
        <h5>{{ isFiltered ? 'Нет данных соответствующих фильтрам.' : 'Нет созданных исполнителей.' }}</h5>
        <div v-if="!isFiltered">
          <p>Создайте нового исполнителя прямо сейчас</p>
          <b-btn v-if="canEditWorkers()" class="custom-btn-primary mt-1" :disabled="isBusy" @click="onCreate()">
            <v-icon name="buttons/plus" :size="12" class="mr-1"/>
            Новый исполнитель
          </b-btn>
          </div>
      </div>
    </div>

    <dynamic-tables
      storage-key="workers_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />
  </div>
</template>

<script>
import workers from "../../network/workers";
import catalogs from "../../network/catalogs";
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";

export default {
  name: "WorkerTable",
  components: { TablesSkeleton, PaginationFooter, ExportSidebar, FiltersDrawer, DynamicTables },
  mixins: [
    workers, catalogs,
    helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Исполнители',
      isBusy: false,
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class:'workers-select'
      },
      fields: [
        {
          key: 'id',
          label: 'ID',
          class: 'workers-id',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'name',
          label: this.$t('message.workers.table.name'),
          class: 'workers-name',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'phone',
          label: this.$t('message.workers.table.phone'),
          class: 'workers-phone',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'address',
          label: this.$t('message.workers.table.address'),
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'passport_url',
          label: this.$t('message.workers.table.passport'),
          sortable: true,
          formatter: 'existPassport',
          class: 'workers-passport',
          visible: true
        },
        {
          key: 'sex',
          label: 'Пол',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'passport_type',
          label: 'Гражданство',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'age',
          label: this.$t('message.workers.table.age'),
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'skill',
          label: this.$t('message.workers.table.skill'),
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'ready',
          label: this.$t('message.workers.table.ready'),
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'level_efficiency',
          label: 'Ур.КПД',
          sortable: true,
          visible: true,
          exportable: true
        },
        {
          key: 'employment_type',
          label: "Тип занятости",
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'employment_type_smz',
          label: "СМЗ",
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'active',
          label: "Активный",
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'status_id',
          label: "Статус",
          sortable: true,
          visible: true,
          exportable: true,
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.workers.table.actions'),
        class: 'workers-action'
      },
      additionalExportFields: [
        {
          key: 'card_number',
          label: "Карта",
          visible: true,
          exportable: true,
        },
      ],
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      towns: [],
      workerStatuses: [],
      deleteRow: {
        id: null,
        name: '',
      },
      levelEfficiencyOptions: [
        { value: '', text: 'Все уровни' },
        { value: 'high', text: 'Высокий' },
        { value: 'medium', text: 'Средний' },
        { value: 'low', text: 'Низкий' },
      ],
      employmentTypes: [
        { value: null, text: 'Не выбран' },
        { value: 1, text: 'СМЗ' },
        { value: 2, text: 'ГПХ' },
        { value: 3, text: 'ФЛ' },
        { value: 4, text: 'ИП' },
        { value: 5, text: 'В штате' },
      ],
      selected: [],
      allSelected: false,
      indeterminate: false,
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.town) {
        this.filters.town = query.town;
      }

      if (query.active) {
        this.filters.active = query.active === 'all' ? null : query.active;
      }

      if (query.level_efficiency) {
        this.filters.level_efficiency = query.level_efficiency;
      }

      if (query.status_id) {
        this.filters.status_id = query.status_id;
      }
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    this.towns = (await this.getTowns({})).data.items;
    this.workerStatuses = (await this.getWorkerStatuses({})).data.items;
    await this.onSearch();
  },
  computed: {
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        this.serviceStartField,
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    dataToExport() {
      return this.selected.length > 0 ? this.selected : this.items;
    },
    exportableFields() {
      return [...this.fields, ...this.additionalExportFields]
        .filter(field => field.exportable)
        .map(field => ({
          key: field.key,
          label: field.label,
          visible: field.visible,
        }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();

      if (this.filters.town !== defaults.town) {
        const foundItem = this.towns.find(s => s.id === this.filters.town);
        tags.push({ key: 'town', label: foundItem ? foundItem.name : 'Город' });
      }

      if (this.filters.active !== defaults.active) {
        tags.push({ key: 'active', label: 'Активные' });
      }

      if (this.filters.level_efficiency !== defaults.level_efficiency) {
        const foundItem = this.levelEfficiencyOptions.find(s => s.value === this.filters.level_efficiency);
        tags.push({ key: 'level_efficiency', label: foundItem ? foundItem.text : 'Уровень КПД' });
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.workerStatuses.find(s => s.value === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.text : 'Статус' });
      }

      return tags;
    },
    isFiltered() {
      const hasQuery = !!this.filters.query && this.filters.query.trim().length > 0;
      const hasTags = this.activeFilterTags.length > 0;

      return hasQuery || hasTags;
    },
  },
  watch: {
    selected(newValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.items.length) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }
    },
  },
  methods: {
    async getItems() {
      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        town: this.filters.town,
        active: this.filters.active,
        status_id: this.filters.status_id,
        level_efficiency: this.filters.level_efficiency,
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
      }

      this.isBusy = true;
      const result = await this.getWorkers(data);

      if (!result || !result.data) {
        this.items = [];
        this.isBusy = false;
        this.totalRows = 0;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.items;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    onRowDblClicked(item) {
      if (this.hasPermission([PERM.WORKER_EDIT])) {
        this.onEdit(item);
        return;
      }

      if (this.hasPermission([PERM.WORKER_READ])) {
        this.onView(item);
        return;
      }
    },
    onEdit(item) {
      this.$router.push({ name: 'WorkersEdit', params: { worker_id: item.id, isRead: false }}).catch(()=>{});
    },
    onView(item) {
      this.$router.push({ name: 'WorkersView', params: { worker_id: item.id, isRead: true }}).catch(()=>{});
    },
    onCreate() {
      this.$router.push({ path: 'workers/new'}).catch(()=>{});
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = this.$t('message.workers.modal.removeTitle');
      const message = `${this.$t("message.workers.modal.removeDesc")} ${this.deleteRow.name}?`;
      this.showModalDelete(title, message, async () => {

        const result = await this.deleteWorker(this.deleteRow);

        if (result.error) {
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Исполнитель удалён.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    getLevelEfficiencyText(value) {
      const option = this.levelEfficiencyOptions.find(opt => opt.value === value);
      return option.text;
    },
    getLevelEfficiencyStyle(value) {
      switch (value) {
        case 'high':
          return 'tag-success';
        case 'medium':
          return 'tag-warning';
        case 'low':
          return 'tag-danger';
        default:
          return '';
      }
    },
    getEmploymentTypeText(value) {
      const option = this.employmentTypes.find(opt => opt.value === value);
      return option.text;
    },
    // Filters
    getDefaultFilters() {
      return {
        query: '',
        town: -1,
        active: true,
        status_id: null,
        level_efficiency: '',
      }
    },
    removeFilterTag(tag) {
      if (tag.key === 'town') {
        this.filters.town = -1;
      }
      else if (tag.key === 'active') {
        this.filters.active = true;
      }
      else if (tag.key === 'status') {
        this.filters.status_id = null;
      }
      else if (tag.key === 'level_efficiency') {
        this.filters.level_efficiency = '';
      }
      this.onSearch();
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();
      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }
      this.filters = filters;
      this.onSearch();
    },
    // Export
    async downloadFile(id, passportUrl) {
      const fileName = passportUrl.split("/").pop();
      const result = await this.getWorkerFile(id);

      if (result.error || !result.data) {
        this.showError("Ошибка загрузки файла", result.error.message);
        return;
      }

      this.handleFileDownload(result.data, fileName);
    },
    existPassport(value) {
      return value.length > 0;
    },
    mapWorkerForExport(item) {
      return {
        id: item.id,
        name: item.name,
        phone: item.phone,
        address: item.address,
        card_number: (item.payments_card && item.payments_card.card_number) ? item.payments_card.card_number : '',
        sex: item.sex === true ? 'Мужчина' : 'Женщина',
        passport_type: item.passport_type,
        age: item.age,
        skill: item.skill,
        ready: item.ready,
        level_efficiency: this.getLevelEfficiencyText(item.level_efficiency),
        employment_type: this.getEmploymentTypeText(item.employment_type),
        employment_type_smz: item.employment_type === 1 ? 'Да' : 'Нет',
        active: item.active ? 'Да' : 'Нет',
        status_id: item.status_name
      };
    },
    async selectPageRows(checked) {
      this.selected = checked ? [...this.items] : [];
      this.updateSelected();
    },
    onRowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
      } else {
        this.selected.push(item);
      }
      this.updateSelected();
    },
    updateSelected() {
      if (!this.items) return;

      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Permissions
    canEditWorkers() {
      return this.hasPermission([PERM.WORKER_EDIT]);
    }
  },
}
</script>

<style>
@media screen
and (min-width: 768px) {
  a[href*="tel:"] {
    pointer-events: none;
    cursor: default;
    text-decoration: none;
  }
}

#workers td {
  text-align: center;
  vertical-align: middle;
}

#workers th {
  text-align: center;
  vertical-align: middle;
}

.workers-select {
  width: 50px;
}

.workers-id {
  width: 200px;
}

.workers-name {
  min-width: 160px;
}

.workers-passport {
  width: 80px;
}

.workers-action {
  width: 80px;
  padding-right: 10px !important;
}

.workers-phone {
  min-width: 130px;
}
</style>



// WEBPACK FOOTER //
// src/components/Workers/WorkerTable.vue